import { useState, useCallback, useEffect } from 'react';
import type { PopupProps } from '@CS/components/Popups';
import { useLoader, useTranslation } from '@CS/hooks';
import { Popup } from '@CS/components/Popups';
import { LeaderBoardWeeks } from './Weeks';
import CSS from './Leaderboard.module.css';
import { useClassNames } from '@CS/hooks';
import Content from './content';

type HighScores = readonly {
  name?: string;
  score: number;
}[];

type UserHighScore = Required<HighScores[number]> & {
  position: number;
};

export type WeekScores = (week: number) => Promise<{
  userData: UserHighScore;
  weekScores: HighScores;
}>;

type LeaderboardProps = PopupProps & {
  getWeekScores: WeekScores;
  duplicateUser?: boolean;
  currentWeek?: number;
  popup?: boolean;
  weeks?: boolean;
};

export const Leaderboard = ({
  duplicateUser = true,
  currentWeek = 1,
  getWeekScores,
  popup = true,
  weeks = true,
  ...props
}: LeaderboardProps) => {
  const { title } = useTranslation(Content);
  const [loading, setLoading] = useState(true);

  const [scores, setScores] = useState<HighScores>([]);
  const [userData, setUserData] = useState<UserHighScore>();

  const onWeekClick = (week: number) => setSelectedWeek(week);
  const [visibleUserScore, setVisibleUserScore] = useState(false);

  const [selectedWeek, setSelectedWeek] = useState(currentWeek - 1);
  const loader = useLoader(loading, { color: 'black', opacity: 0.2 });

  const updateSelectedWeek = useCallback(async () => {
    const { userData, weekScores } = await getWeekScores(selectedWeek);

    setVisibleUserScore(
      !!userData && !duplicateUser
        ? userData?.position > weekScores.length
        : !!userData
    );

    if (!userData.name || userData.position < 0) {
      setVisibleUserScore(false);
    }

    setUserData(userData);
    setScores(weekScores);
  }, [selectedWeek]);

  const containerClassNames = useClassNames([
    CSS.leaderboardContainer,
    popup && CSS.popup
  ]);

  const scoreClassNames = useClassNames([
    !!userData && CSS.user,
    CSS.scoreContainer
  ]);

  useEffect(() => {
    setLoading(true);
    updateSelectedWeek().finally(() => setLoading(false));
  }, [selectedWeek]);

  const leaderBoard = (
    <div className={containerClassNames}>
      <h2 className={CSS.leaderboardTitle}>{title}</h2>

      {weeks && (
        <LeaderBoardWeeks
          current={selectedWeek}
          onClick={onWeekClick}
          weeks={currentWeek}
        />
      )}

      <ul className={CSS.leaderboardScores}>
        {scores.map((user, u) => (
          <li key={u} className={CSS.scoreContainer}>
            <pre className={CSS.userPosition}>{`${u + 1}.`}</pre>

            <div className={CSS.userScoreList}>
              <span className={CSS.userName}>{user.name}</span>
              <em className={CSS.userScore}>{user.score}</em>
            </div>
          </li>
        ))}

        {userData && visibleUserScore && (
          <>
            <hr className={CSS.userSeparator} />

            <li className={scoreClassNames}>
              <pre className={CSS.userPosition}>{userData.position}.</pre>

              <div className={CSS.userScoreList}>
                <span className={CSS.userName}>{userData.name}</span>
                <em className={CSS.userScore}>{userData.score}</em>
              </div>
            </li>
          </>
        )}
      </ul>

      {loader}
    </div>
  );

  return popup ? (
    <Popup {...props}>{leaderBoard}</Popup>
  ) : (
    <section className={CSS.leaderboardPage}>{leaderBoard}</section>
  );
};
